import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer, Scatter, ScatterChart, 
  ZAxis, ComposedChart, Bar, ReferenceLine, Label
} from 'recharts';
import '../css/DegreeDaysUsageWidget.css';
import api from './api';

/**
 * Widget that compares degree days with energy usage to demonstrate efficiency
 * Uses data from both degree-days and kenter endpoints
 * UPDATED: Shows usage vs efficiency instead of usage vs degree days
 * IMPROVED: Better mobile responsiveness and readability
 * FIXED: Now properly displays data points for all years in correlation chart
 */
const DegreeDaysUsageWidget = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({});
  const [correlationData, setCorrelationData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [efficiencyData, setEfficiencyData] = useState(null);
  const [displayMode, setDisplayMode] = useState('monthly'); // 'monthly' or 'correlatie'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [allDegreeDays, setAllDegreeDays] = useState({});
  const chartContainerRef = useRef(null);
  
  // Get current energy year for reference
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // JS months are 0-indexed
  const currentYear = currentDate.getFullYear();
  const currentEnergyYear = currentMonth >= 7 ? 
    `${currentYear}-${currentYear+1}` : 
    `${currentYear-1}-${currentYear}`;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Fetch both degree days and energy usage data
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First, fetch energy usage data to get available years
      const usageResponse = await api.get('/api/kenter/usage');
      
      // Identify available energy years
      const energyYears = Object.keys(usageResponse.data)
        .filter(year => year.match(/^\d{4}-\d{4}$/))
        .sort();
      
      setAvailableYears(energyYears);
      
      // Default to the most recent energy year
      const selectedEnergyYear = energyYears[energyYears.length - 1];
      setSelectedYear(selectedEnergyYear);
      
      // Fetch degree days data for ALL energy years
      const degreePromises = energyYears.map(year => 
        api.get('/api/degree-days/monthly', { params: { energyYear: year } })
          .then(response => ({ year, data: response.data }))
          .catch(error => ({ year, data: [], error }))
      );
      
      // Wait for all degree day data to be fetched
      const degreeResponses = await Promise.all(degreePromises);
      
      // Create a mapping of all degree days data by month
      const degreeDaysMap = {};
      
      // Process each year's degree days data
      degreeResponses.forEach(response => {
        const { year, data, error } = response;
        
        if (error) {
          console.warn(`Warning: Failed to fetch degree days for ${year}`, error);
          return;
        }
        
        // Add each month's degree days to the map
        data.forEach(item => {
          const month = item._id; // Format: YYYY-MM
          degreeDaysMap[month] = item.totalDegreeDays;
        });
      });
      
      // Store the combined data for reference
      setAllDegreeDays(degreeDaysMap);
      
      // Also get efficiency data for reference
      const efficiencyResponse = await api.get('/api/degree-days/efficiency');
      
      // Process the data with ALL degree days data
      processData(degreeDaysMap, usageResponse.data);
      setEfficiencyData(efficiencyResponse.data);
      
      console.log('✅ Successfully fetched data for all years', energyYears);
      console.log('📊 Degree days data available for months:', Object.keys(degreeDaysMap).length);
      
    } catch (err) {
      console.error('❌ Error fetching degree days and usage data:', err);
      setError('Kon de gegevens niet laden. Probeer het later nog eens.');
    } finally {
      setLoading(false);
    }
  };
  
  // Process and combine degree days and energy usage data
  const processData = (degreesByMonth, usageData) => {
    console.log('🔄 Processing data with degree days:', degreesByMonth);
    
    // Next, collect energy usage by month
    const energyYears = Object.keys(usageData).filter(year => 
      year.match(/^\d{4}-\d{4}$/) && // Filter valid energy year formats
      usageData[year].monthly // Ensure we have monthly data
    );
    
    setAvailableYears(energyYears);
    
    // Create combined chart data for each available energy year
    const combinedData = {};
    const correlationPoints = [];
    
    energyYears.forEach(energyYear => {
      // Get the monthly data for this energy year
      const monthlyData = usageData[energyYear].monthly || {};
      
      // Prepare data for this energy year
      combinedData[energyYear] = [];
      
      // Process each month
      Object.keys(monthlyData).forEach(month => {
        // Get the usage and degree days for this month
        const usage = monthlyData[month] || 0;
        const degreeDays = degreesByMonth[month] || 0;
        
        // Calculate efficiency (m³ per degree day)
        const efficiency = degreeDays > 0 ? usage / degreeDays : 0;
        
        // Create a data point for the chart
        const dataPoint = {
          month: formatMonthLabel(month),
          rawMonth: month,
          usage: usage,
          degreeDays: degreeDays,
          efficiency: parseFloat(efficiency.toFixed(2))
        };
        
        // Add to combined data
        combinedData[energyYear].push(dataPoint);
        
        // Add to correlation data only if both values exist
        if (usage > 0 && degreeDays > 0) {
          correlationPoints.push({
            energyYear,
            degreeDays,
            usage,
            efficiency: parseFloat(efficiency.toFixed(2)),
            month: formatMonthLabel(month),
            rawMonth: month
          });
        }
      });

      // Sort by month (handle energy year wrap July-June)
      combinedData[energyYear].sort((a, b) => {
        const [yearA, monthA] = a.rawMonth.split('-').map(Number);
        const [yearB, monthB] = b.rawMonth.split('-').map(Number);
        
        // Convert to energy year order (July = 1, August = 2, ..., June = 12)
        const orderA = monthA >= 7 ? monthA - 6 : monthA + 6;
        const orderB = monthB >= 7 ? monthB - 6 : monthB + 6;
        
        if (yearA !== yearB) return yearA - yearB;
        return orderA - orderB;
      });
    });

    console.log('📊 Generated correlation data points:', correlationPoints.length);
    console.log('📈 Sample correlation data:', correlationPoints.slice(0, 3));
    
    // Set the chart data
    setChartData(combinedData);
    setCorrelationData(correlationPoints);
  };

  // Add a year change handler
  const handleYearChange = async (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
  };
  
  // Format month label for display
  const formatMonthLabel = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    // Use shorter month format on mobile devices
    return date.toLocaleString('nl-NL', { 
      month: isMobile ? 'narrow' : 'short' 
    });
  };
  
  // Format number with thousands separator
  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    return new Intl.NumberFormat('nl-NL').format(Math.round(num));
  };
  
  // Toggle display mode between charts
  const toggleDisplayMode = (mode) => {
    setDisplayMode(mode);
  };
  
  // Generate tooltip content
  const renderTooltip = (props) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }

    const data = props.payload[0].payload;
    
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{data.month}</p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Verbruik:</span> {formatNumber(data.usage)} m³
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Graaddagen:</span> {formatNumber(data.degreeDays)}
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Efficiëntie:</span> {data.efficiency} m³/graaddag
        </p>
      </div>
    );
  };
  
  // Render correlation tooltip
  const renderCorrelationTooltip = (props) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }

    const data = props.payload[0].payload;
    
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{data.month} ({data.energyYear})</p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Verbruik:</span> {formatNumber(data.usage)} m³
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Graaddagen:</span> {formatNumber(data.degreeDays)}
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">Efficiëntie:</span> {data.efficiency} m³/graaddag
        </p>
      </div>
    );
  };
  
  // Calculate trend line data (simple linear regression)
  const calculateTrendLine = () => {
    if (!correlationData || correlationData.length < 2) return [];
    
    // Extract x and y values
    const points = correlationData.map(d => ({
      x: d.degreeDays,
      y: d.usage
    }));
    
    // Calculate means
    const n = points.length;
    const meanX = points.reduce((sum, p) => sum + p.x, 0) / n;
    const meanY = points.reduce((sum, p) => sum + p.y, 0) / n;
    
    // Calculate slope (m) and y-intercept (b)
    let numerator = 0;
    let denominator = 0;
    
    for (const point of points) {
      numerator += (point.x - meanX) * (point.y - meanY);
      denominator += (point.x - meanX) * (point.x - meanX);
    }
    
    const slope = denominator !== 0 ? numerator / denominator : 0;
    const intercept = meanY - slope * meanX;
    
    // Generate trend line points
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    
    return [
      { x: minX, y: slope * minX + intercept },
      { x: maxX, y: slope * maxX + intercept }
    ];
  };
  
  // Get dynamic chart height based on screen size
  const getChartHeight = () => {
    if (isMobile) {
      return 230; // Smaller height on mobile but still readable
    }
    return 250; // Default height for desktop
  };
  
  if (loading) {
    return (
      <div className="degree-days-usage-widget card">
        <h3>🌡️ Graaddagen vs. Verbruik</h3>
        <div className="tiny-loading-indicator">
  <div className="micro-spinner"></div>
  <span>Laden...</span>
</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="degree-days-usage-widget card">
        <h3>🌡️ Graaddagen vs. Verbruik</h3>
        <div className="error-message">
          <p>{error}</p>
          <button className="sync-button" onClick={fetchData}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }
  
  // Calculate trend line for correlation chart
  const trendLine = calculateTrendLine();
  
  // Debug - log correlation data by energy year
  const dataByYear = {};
  availableYears.forEach(year => {
    dataByYear[year] = correlationData.filter(d => d.energyYear === year).length;
  });
  console.log('Data points by year:', dataByYear);
  
  return (
    <div className="degree-days-usage-widget card">
      <div className="widget-header">
        <h3>🌡️ Graaddagen vs. Verbruik</h3>
      </div>
      
      {displayMode === 'monthly' && selectedYear && chartData[selectedYear] && (
        <>
          <div className="year-selector">
            <label>Stookseizoen: </label>
            <select 
              value={selectedYear} 
              onChange={handleYearChange}
              className="year-select"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div className="chart-container2" ref={chartContainerRef}>
            <ResponsiveContainer width="100%" height={getChartHeight()}>
              <ComposedChart 
                data={chartData[selectedYear]}
                margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 5 } : { top: 10, right: 20, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  tickMargin={isMobile ? 5 : 10}
                />
                <YAxis 
                  yAxisId="left" 
                  orientation="left" 
                  stroke="#3B82F6"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  tickFormatter={value => isMobile ? value / 1000 + 'k' : value}
                  width={isMobile ? 30 : 40}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  stroke="#10B971"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 25 : 40}
                />
                <Tooltip content={renderTooltip} />
                <Legend 
                  wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                  verticalAlign={isMobile ? "top" : "bottom"}
                />
                <Bar 
                  yAxisId="left" 
                  dataKey="usage" 
                  name="Verbruik (m³)" 
                  fill="#3B82F6" 
                  barSize={isMobile ? 15 : 20}
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="efficiency" 
                  name="Efficiëntie (m³/gr.dag)" 
                  stroke="#000000" 
                  strokeWidth={2}
                  dot={{ r: isMobile ? 1 : 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
      
      {displayMode === 'correlatie' && (
        <div className="chart-container2">
          <div className="widget-description">
            Deze grafiek toont de relatie tussen graaddagen (een maat voor de kou) en het gasverbruik.
          </div>
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
            <ScatterChart 
              margin={isMobile ? 
                { top: 5, right: 10, bottom: 40, left: 10 } : 
                { top: 10, right: 20, bottom: 40, left: 10 }
              }
            >
              <CartesianGrid />
              <XAxis 
                type="number" 
                dataKey="degreeDays" 
                name="Graaddagen" 
                unit=""
                tick={{ fontSize: isMobile ? 10 : 12 }}
                domain={[0, 'auto']}
                tickCount={5}
                allowDecimals={false}
              >
                <Label 
                  value="Graaddagen" 
                  offset={-5} 
                  position="insideBottom"
                  style={{ fontSize: isMobile ? 10 : 12 }}
                />
              </XAxis>
              <YAxis 
                type="number" 
                dataKey="usage" 
                name="Verbruik" 
                unit=" m³"
                tick={{ fontSize: isMobile ? 10 : 12 }}
              >
                <Label 
                  value="Verbruik (m³)" 
                  angle={-90} 
                  position="insideLeft"
                  style={{ textAnchor: 'middle', fontSize: isMobile ? 10 : 12 }}
                />
              </YAxis>
              <Tooltip active={false} cursor={false} />
              <Legend 
                wrapperStyle={{ 
                  fontSize: isMobile ? 10 : 12,
                  width: '100%',
                  margin: '0 auto',
                  textAlign: 'center',
                  paddingTop: 20
                }}
                verticalAlign="bottom"
                layout={isMobile ? "vertical" : "horizontal"}
                align="center"
                iconSize={10}
                iconType="circle"
              />
              
              {/* Draw trend line */}
              {trendLine.length > 0 && (
                <Line 
                  data={trendLine} 
                  type="linear" 
                  dataKey="y" 
                  stroke="#000000" 
                  strokeWidth={1.5}
                  dot={false} 
                  activeDot={false}
                  name="Trendlijn"
                  xAxisId={0}
                  yAxisId={0}
                />
              )}
              
              {/* Plot points for each energy year with different colors */}
              {availableYears.map((year, index) => {
                // Use a palette of colors that are visually distinct
                const colorPalette = [
                  '#3B82F6', // Blue - 2022-2023
                  '#EF4444', // Red - 2023-2024
                  '#10B981', // Green - 2024-2025
                  '#F59E0B', // Amber
                  '#8B5CF6', // Purple
                  '#EC4899', // Pink
                  '#14B8A6', // Teal
                  '#6366F1'  // Indigo
                ];
                
                const color = colorPalette[index % colorPalette.length];
                
                // Get data points for this year
                const yearData = correlationData.filter(d => d.energyYear === year);
                
                return (
                  <Scatter 
                    key={year}
                    name={`${year}`}
                    data={yearData}
                    fill={color}
                    shape="circle"
                    isAnimationActive={false}
                    activeDot={false}
                  />
                );
              })}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
      
      <div className="widget-controls">
        <button 
          className={`mode-toggle ${displayMode === 'monthly' ? 'active' : ''}`}
          onClick={() => toggleDisplayMode('monthly')}
        >
          Maandelijks
        </button>
        <button 
          className={`mode-toggle ${displayMode === 'correlatie' ? 'active' : ''}`}
          onClick={() => toggleDisplayMode('correlatie')}
        >
          Correlatie
        </button>
      </div>
      
      <Link to="/degree-days" className="view-details-button details-btn">
        Bekijk Details
      </Link>
    </div>
  );
};

export default DegreeDaysUsageWidget;